import { Attribute, Filters, LocalFilters } from '~app/modules/filters/models';
import isEmpty from 'lodash.isempty';
import cloneDeep from 'lodash.clonedeep';
import { Category } from '~app/modules/generic/models';
import { NUMBER_0 } from '~shared/constants';
import { AttributeOption } from '~shared/models';

export namespace FiltersStateUtil {

  function getMergedFilters(localFiltersItems, serverFiltersItems = []): Category[] {
    const ids = new Set(localFiltersItems.map(item => item.id));
    return [ ...localFiltersItems, ...serverFiltersItems.filter(item => !ids.has(item.id)) ];
  }

  export function isCategoriesSelected(paramFilters: { [key: string]: any }): boolean {
    return !!getCategoriesSlugs(paramFilters);
  }

  export function isAttributesSelected(paramFilters: { [key: string]: any }): boolean {
    const { options = {} } = paramFilters || {};
    return !isEmpty(options) || !!Object.keys(paramFilters)?.length;
  }

  export function getCategoriesSlugs(paramFilters: { [key: string]: any }) {
    return paramFilters && paramFilters.categories;
  }

  export function getCategories(localFilters: LocalFilters, serverFilters: Filters,
                                paramFilters: { [key: string]: any }): Category[] {
    const categorySlugs = getCategoriesSlugs(paramFilters);
    const categories: Category[] = getMergedFilters(localFilters.categories, serverFilters.categories);
    const mappedCategorySlugs = categorySlugs
      .map((slug: string) => categories.find((c: Category) => c.slug === slug));

    return (mappedCategorySlugs || []).filter((category: Category) => !isEmpty(category));
  }

  export function getMergedAttributes(localFiltersItems: Attribute[], serverFiltersItems: Attribute[]) {
    if (serverFiltersItems.length > NUMBER_0) {
      return serverFiltersItems
        .map(serverAttribute => {
          const localAttribute = { ...localFiltersItems.find(attr => serverAttribute.id === attr.id) };
          if (isEmpty(localAttribute)) {
            return serverAttribute;
          }

          const localAttributeOptionsIds = new Set(localAttribute.options.map(d => d.id));
          return {
            ...localAttribute,
            options: [
              ...localAttribute.options,
              ...(serverAttribute.options || []).filter(d => !localAttributeOptionsIds.has(d.id))
            ]
          } as Attribute;
        });
    }

    return localFiltersItems;
  }

  export function getAttributes(localFilters: LocalFilters, serverFilters: Filters,
                                paramFilters: { [key: string]: any }): Attribute[] {
    const attributes = getMergedAttributes(localFilters.attributes, serverFilters.attributes);
    const { options = {} } = paramFilters;

    if (isEmpty(options)) {
      return [];
    }
    const attributesKeys = Object.keys(options);
    const mappedAttributes: Attribute[] = attributesKeys
      .map(key => {
        const selectedAttribute = { ...attributes.find(attribute => attribute.id === key) };
        if (isEmpty(selectedAttribute)) {
          return null;
        }
        const attributeValues = options[`${key}`].map(val => val.toString());
        return {
          ...selectedAttribute,
          options: (selectedAttribute.options || []).filter(opt => attributeValues.includes(opt.value))
        } as Attribute;
      });
    return (mappedAttributes || []).filter((attr: Attribute) => !!attr && !isEmpty(attr.options));
  }

  export function getAttributeWithSelectedOption(attributes: Attribute[], attrKey: string,
                                         attrOptionValue): Attribute {
    const attribute = attributes.find(attr => attr.id === attrKey);

    if (!attribute) {
      return null;
    }

    const clonedAttribute: Attribute = cloneDeep(attribute);

    clonedAttribute.options = (clonedAttribute.options || []).filter((foundAttrOption: AttributeOption) =>
      foundAttrOption.value === attrOptionValue.toString());

    return clonedAttribute;
  }
}
